/**
 * Feb 2026 — Display-only suburb rosters per region cluster.
 *
 * Purpose: give every RegionHub a populated "Suburbs we test in"
 * grid without requiring a full landing page for each entry in
 * `locations.json`. Where a suburb DOES have a published landing
 * page (e.g. Byron Bay, Ballina), RegionHub links to it — the rest
 * render as plain chips.
 *
 * Keys are `cluster.key` values from `locationTree.js` REGIONS.
 * Adding a suburb here does not create a landing page — it only
 * signals coverage for SEO and consumer clarity on the region page.
 */

export const REGION_CLUSTER_SUBURBS = {
  // Sunshine Coast (QLD)
  "noosa-hinterland":       ["Cooroy", "Cooroibah", "Cooran", "Doonan", "Eumundi", "Federal", "Kin Kin", "Pomona", "Ridgewood", "Tewantin", "Boreen Point"],
  "coolum-peregian":        ["Coolum Beach", "Marcus Beach", "Peregian Beach", "Peregian Springs", "Point Arkwright", "Yaroomba", "Sunshine Beach", "Sunrise Beach"],
  "maroochy-mooloolah":     ["Buderim", "Diddillibah", "Ilkley", "Mons", "Mooloolah Valley", "Mountain Creek", "Palmwoods", "Sippy Downs", "Tanawha", "Woombye"],
  "hinterland-glasshouse":  ["Beerwah", "Bells Creek", "Glasshouse Mountains", "Maleny", "Mooloolah", "Peachester", "Landsborough", "Bald Knob"],

  // Northern Rivers (NSW)
  "byron-shire":            ["Bangalow", "Brunswick Heads", "Byron Bay", "Federal", "Mullumbimby", "Ocean Shores", "Suffolk Park", "Myocum"],
  "ballina-shire":          ["Alstonville", "Ballina", "Lennox Head", "Wardell", "Wollongbar", "East Ballina", "Cabbage Tree Island"],
  "lismore-region":         ["Bexhill", "Clunes", "Lismore", "Modanville", "Nimbin", "Rosebank", "Goonellabah", "Dunoon"],
  "tweed-shire":            ["Cabarita Beach", "Chinderah", "Kingscliff", "Murwillumbah", "Pottsville", "Uki", "Fingal Head", "Casuarina"],

  // Toowoomba & Darling Downs (QLD)
  "toowoomba-city":         ["Toowoomba", "Highfields", "Kearneys Spring", "Rangeville", "Middle Ridge", "Newtown", "Wilsonton", "Centenary Heights", "Glenvale", "Cotswold Hills"],
  "darling-downs-east":     ["Oakey", "Pittsworth", "Cambooya", "Clifton", "Millmerran", "Yarraman", "Crows Nest", "Goombungee"],
  "southern-downs":         ["Warwick", "Killarney", "Stanthorpe", "Allora", "Applethorpe", "Ballandean", "Wallangarra", "Yangan"],

  // Dayboro & Moreton Bay Hinterland (QLD)
  "dayboro-village":        ["Dayboro", "Kurwongbah", "Mount Mee", "Ocean View", "Rush Creek", "Whiteside", "Armstrong Creek"],
  "samford-valley":         ["Samford", "Camp Mountain", "Cedar Creek", "Highvale", "Wights Mountain", "Yugar", "Samford Village", "Closeburn"],
  "kilcoy-hinterland":      ["Kilcoy", "Woodford", "Mount Kilcoy", "D'Aguilar", "Villeneuve", "Winya", "Sandy Creek", "Mount Archer"],

  // Redland Bay & Logan (QLD)
  "redlands":               ["Cleveland", "Victoria Point", "Redland Bay", "Wellington Point", "Ormiston", "Thornlands", "Alexandra Hills", "Capalaba"],
  "moreton-bay-islands":    ["Russell Island", "Macleay Island", "Lamb Island", "Karragarra Island", "Coochiemudlo Island", "North Stradbroke"],
  "logan-north":            ["Springwood", "Beenleigh", "Loganholme", "Waterford", "Shailer Park", "Rochedale South", "Daisy Hill", "Underwood"],

  // Tweed / Byron Border Coast (NSW)
  "border-coastal":         ["Tweed Heads", "Banora Point", "Kingscliff", "Cabarita Beach", "Chinderah", "Terranora", "Bilambil", "Bilambil Heights", "Fingal Head", "Casuarina"],
  "tweed-valley-hinterland":["Murwillumbah", "Uki", "Chillingham", "Tumbulgum", "Tyalgum", "Bray Park", "Nunderi", "Mount Warning"],
  "byron-hinterland":       ["Byron Bay", "Suffolk Park", "Mullumbimby", "Ocean Shores", "Bangalow", "Federal", "Brunswick Heads", "New Brighton"],

  // Central Coast NSW
  "gosford-region":         ["Gosford", "East Gosford", "Woy Woy", "Umina Beach", "Erina", "Terrigal", "Kincumber", "Avoca Beach"],
  "wyong-lakes":            ["Wyong", "The Entrance", "Toukley", "Long Jetty", "Bateau Bay", "Berkeley Vale", "Killarney Vale", "Budgewoi"],
  "central-coast-plateau":  ["Mangrove Mountain", "Somersby", "Kulnura", "Peats Ridge", "Wyong Creek", "Ourimbah", "Dooralong"],

  // Riverina & Southern Tablelands (NSW)
  "riverina-central":       ["Wagga Wagga", "Junee", "Cootamundra", "Coolamon", "Temora", "Tumut", "Gundagai", "Ganmain"],
  "riverina-mia":           ["Griffith", "Leeton", "Yenda", "Hillston", "Yanco", "Coleambally", "Darlington Point", "Whitton"],
  "southern-tablelands":    ["Goulburn", "Yass", "Crookwell", "Braidwood", "Bungendore", "Marulan", "Taralga", "Gunning"],

  // Bathurst & Central Tablelands (NSW)
  "bathurst-region":        ["Bathurst", "Kelso", "Eglinton", "Perthville", "O'Connell", "Rockley", "Raglan", "Wattle Flat", "Sofala", "Trunkey Creek"],
  "orange-cabonne":         ["Orange", "Millthorpe", "Blayney", "Molong", "Canowindra", "Cudal", "Manildra", "Cargo", "Spring Hill", "Lucknow"],
  "lithgow-oberon":         ["Lithgow", "Portland", "Wallerawang", "Oberon", "Black Springs", "Tarana", "Rydal", "Hampton", "Capertee", "Cullen Bullen"],

  // Tropical North Queensland
  "cairns-region":          ["Cairns", "Edmonton", "Trinity Beach", "Palm Cove", "Kewarra Beach", "Redlynch", "Gordonvale", "Smithfield"],
  "port-douglas-daintree":  ["Port Douglas", "Mossman", "Daintree", "Cape Tribulation", "Wonga Beach", "Newell", "Craiglie"],
  "atherton-tablelands":    ["Atherton", "Malanda", "Yungaburra", "Ravenshoe", "Tolga", "Herberton", "Millaa Millaa", "Kairi"],

  // Gold Coast South & North (QLD)
  "gold-coast-north":       ["Southport", "Surfers Paradise", "Broadbeach", "Runaway Bay", "Labrador", "Main Beach", "Bundall", "Ashmore"],
  "gold-coast-south":       ["Burleigh Heads", "Palm Beach", "Currumbin", "Coolangatta", "Tugun", "Miami", "Bilinga", "Elanora"],
  "gold-coast-hinterland":  ["Nerang", "Mudgeeraba", "Springbrook", "Tamborine Mountain", "Numinbah Valley", "Advancetown", "Bonogin", "Worongary"],

  // Cooloola & Fraser Coast (QLD)
  "cooloola-coast":         ["Rainbow Beach", "Tin Can Bay", "Cooloola Cove", "Inskip", "Cooran", "Gympie", "Southside", "Widgee"],
  "hervey-bay-region":      ["Hervey Bay", "Urangan", "Point Vernon", "Torquay", "Scarness", "Pialba", "Kawungan", "Dundowran"],
  "kgari-fraser-island":    ["Kingfisher Bay", "Eurong", "Happy Valley", "Cathedral Beach", "Orchid Beach"],

  // South Gippsland (VIC)
  "bass-coast":             ["Wonthaggi", "Inverloch", "Cape Paterson", "Cowes", "San Remo", "Kilcunda", "Ventnor", "Rhyll"],
  "south-gippsland-inland": ["Leongatha", "Korumburra", "Foster", "Mirboo North", "Meeniyan", "Toora", "Nyora", "Loch"],
  "corner-inlet":           ["Fish Creek", "Waratah Bay", "Sandy Point", "Yanakie", "Tarwin Lower", "Walkerville", "Port Franklin"],

  // Top End (NT)
  "darwin-region":          ["Darwin", "Palmerston", "Humpty Doo", "Howard Springs", "Berry Springs", "Virginia", "Coolalinga", "Bees Creek"],
  "katherine-region":       ["Katherine", "Tindal", "Pine Creek", "Mataranka", "Larrimah", "Manbulloo"],
  "tiwi-arnhem":            ["Wurrumiyanga", "Wadeye", "Nhulunbuy", "Milingimbi", "Maningrida", "Galiwinku"],
};

export function suburbs_for_cluster(clusterKey) {
  return REGION_CLUSTER_SUBURBS[clusterKey] || [];
}
